<script setup lang="ts">
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import { useI18n } from 'vue-i18n'
import { computed, inject } from 'vue'
import slugify from 'slugify'
import { saveAs } from 'file-saver'
import { faBarsSort, faDownload, faFilePdf, faFileWord, faInfoCircle, faPenField, faPrint, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import { KsButton, KsPopover } from '@aschehoug/kloss'
import { PendoFeature } from '~/models/Pendo'
import exportToWordApi from '~/api/exportToWordApi'
import AuTooltip from '~/components/utils/AuTooltip.vue'

const {
  item,
  fields,
  currentImage,
  isFile
} = defineProps<{
  item: ContentFormWriter
  fields: string[]
  currentImage: string
  isFile: boolean
}>()

const showLabel = defineModel<boolean>('showLabel')
const showDescription = defineModel<boolean>('showDescription')
const showPlaceholder = defineModel<boolean>('showPlaceholder')
const showModelText = defineModel<boolean>('showModelText')

const { t } = useI18n()
const { postWord } = exportToWordApi()
const toast = inject('ksToast') as Toast

const showToolBar = computed(() =>
  (item.enableModelText && item.modelText.destinationContentId)
  || item.enablePlaceholder
  || item.enableDescription
  || item.enableLabels
  || item.exportPrint
  || item.exportPdf
  || item.exportWord
  || (isFile && (item.modelText.destinationContentId && item.enableModelText))
)

async function exportToPdf() {
  if (!currentImage && fields.length === 0) {
    return toast.error(t('formWriter.toasts.nothingToExport'))
  }

  try {
    const { jsPDF } = await import('jspdf')
    // eslint-disable-next-line new-cap
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'px'
    })
    doc.setFontSize(12)

    const margin = 24
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const lineHeight = 14
    let y = margin

    if (currentImage) {
      try {
        // For data URLs, use them directly
        if (currentImage.includes(import.meta.env.VITE_MEDIA_URL)) {
          const { width, height } = await new Promise<{ width: number, height: number }>((resolve) => {
            const img = new Image()
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
            img.src = currentImage
          })

          const scaledWidth = Math.min(width, pageWidth - margin * 2)
          const scaledHeight = (height * scaledWidth) / width

          doc.addImage(currentImage, 'JPEG', margin, margin, scaledWidth, scaledHeight)
          y += scaledHeight + margin
        } else {
          // For URL images, use fetch with credentials to get the image data
          const response = await fetch(currentImage, {
            credentials: 'include', // This sends cookies with the request
          })

          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`)
          }

          const blob = await response.blob()

          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.readAsDataURL(blob)
          })

          const { width, height } = await new Promise<{ width: number, height: number }>((resolve) => {
            const img = new Image()
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
            img.src = dataUrl
          })

          const scaledWidth = Math.min(width, pageWidth - margin * 2)
          const scaledHeight = (height * scaledWidth) / width

          doc.addImage(dataUrl, 'JPEG', margin, margin, scaledWidth, scaledHeight)
          y += scaledHeight + margin
        }
      } catch (e) {
        console.error('Image processing error:', e)
        doc.text('Image could not be included in the PDF', margin, y)
        y += lineHeight * 2
      }
    }

    fields.forEach((field) => {
      const lines = doc.splitTextToSize(field, pageWidth - margin * 2)
      lines.forEach((line: string) => {
        if (y + lineHeight > pageHeight) {
          doc.addPage()
          y = margin
        }
        doc.text(line, margin, y)
        y += lineHeight
      })
      y += 5
    })

    doc.save(slugify(item.title))
  } catch (err) {
    console.error('PDF export error:', err)
    toast.error(t('formWriter.toasts.fileDownloadError'))
    throw err
  } finally {
    toast.success(t('formWriter.toasts.fileDownloadSuccess'))
  }
}

async function exportToWord() {
  if (!currentImage && !fields.length) {
    return toast.error(t('formWriter.toasts.nothingToExport'))
  }

  try {
    const response = await postWord({
      image: currentImage,
      fields,
    })
    saveAs(new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    }), `${slugify(item.title)}.docx`)
  } catch (err) {
    toast.error(t('formWriter.toasts.fileDownloadError'))
    throw err
  } finally {
    toast.success(t('formWriter.toasts.fileDownloadSuccess'))
  }
}

const printWindow = () => window.print()
</script>

<template>
  <div v-if="showToolBar" class="-top-20 left-0 flex flex-wrap gap-4 rounded-xl bg-[--theme-10] p-4 md:absolute md:-left-20 md:top-0 md:flex-col print:hidden">
    <AuTooltip
      v-if="item.modelText.destinationContentId && item.enableModelText"
      :content="t('formWriter.tooltips.modelText')"
      side="right"
    >
      <KsButton
        shape="square"
        :variant="showModelText ? 'primary' : 'secondary'"
        :icon-left="faBarsSort"
        :data-pendo="PendoFeature.FormWriter.ShowModelText"
        @click="showModelText = !showModelText"
      />
    </AuTooltip>
    <template v-if="!showModelText && !isFile">
      <AuTooltip
        v-if="item.enableLabels"
        :content="t('formWriter.tooltips.label')"
        side="right"
      >
        <KsButton
          shape="square"
          :variant="showLabel ? 'primary' : 'secondary'"
          :icon-left="faInfoCircle"
          :data-pendo="PendoFeature.FormWriter.ShowLabel"
          @click="showLabel = !showLabel"
        />
      </AuTooltip>
      <AuTooltip
        v-if="item.enableDescription"
        :content="t('formWriter.tooltips.description')"
        side="right"
      >
        <KsButton
          shape="square"
          :variant="showDescription ? 'primary' : 'secondary'"
          :icon-left="faQuestionCircle"
          :data-pendo="PendoFeature.FormWriter.ShowDescription"
          @click="showDescription = !showDescription"
        />
      </AuTooltip>
      <AuTooltip
        v-if="item.enablePlaceholder"
        :content="t('formWriter.tooltips.placeholder')"
        side="right"
      >
        <KsButton
          shape="square"
          :variant="showPlaceholder ? 'primary' : 'secondary'"
          :icon-left="faPenField"
          :data-pendo="PendoFeature.FormWriter.ShowPlaceholder"
          @click="showPlaceholder = !showPlaceholder"
        />
      </AuTooltip>

      <KsPopover
        v-if="item.exportPdf || item.exportWord"
        :title="t('formWriter.tooltips.download')"
        direction="down right"
      >
        <template #toggle>
          <AuTooltip :content="t('formWriter.tooltips.download')" side="right">
            <KsButton
              shape="square"
              variant="secondary"
              :icon-left="faDownload"
            />
          </AuTooltip>
        </template>
        <template #content>
          <div class="flex flex-col gap-2">
            <KsButton
              v-if="item.exportWord"
              variant="secondary"
              :icon-left="faFileWord"
              :data-pendo="PendoFeature.FormWriter.DownloadWord"
              @click="exportToWord"
            >
              {{ t('formWriter.downloadWord') }}
            </KsButton>
            <KsButton
              v-if="item.exportPdf"
              variant="secondary"
              :icon-left="faFilePdf"
              :data-pendo="PendoFeature.FormWriter.DownloadPdf"
              @click="exportToPdf"
            >
              {{ t('formWriter.downloadPdf') }}
            </KsButton>
          </div>
        </template>
      </KsPopover>

      <AuTooltip :content="t('formWriter.tooltips.print')" side="right">
        <KsButton
          v-if="item.exportPrint"
          shape="square"
          variant="secondary"
          :icon-left="faPrint"
          :data-pendo="PendoFeature.FormWriter.Print"
          @click.prevent="printWindow"
        />
      </AuTooltip>
    </template>
  </div>
</template>
